import React, { useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  requestPending,
  requestPristine,
  requestSuccess /*requestError*/,
} from "state/reducer/requestStatusSlice";
import { getContracts } from "net/contracts";
import { Button, Table } from "@ds/ds-snam/react";
import { useNavigate } from "react-router-dom";
import {
  EntityType,
  IAppUserProfile,
  RequestStatus,
  RoutePath,
} from "shared/constants";
import SimpleTable, {
  SimpleTableFamily,
  SimpleTableType,
} from "components/SimpleTable";
import { BadgeStatus, DATE_FORMAT } from "shared/constants";
import TableRighArrow from "components/TableRightArrow";
import NoDataFound from "components/NoDataFound";
import moment from "moment";
import { useTranslation } from "react-i18next";
import useRequestStatus from "hooks/useRequestStatus";
import DsBadge from "components/DsBadge";
import AddEntityButton from "components/AddEntityButton";
import useAppUser from "hooks/useAppUser";
import { IContractElement } from "dataModels/contract";
import { ITableCell, ITableConfig, ITableRow } from "@ds/ds-snam/dist/models/tables";
import { IParseDateFormatType, parseDateForDS } from "shared/utils/dateUtils";
import { getDSTableShaderElementsForFilterColumn,getDSTableSharedElements, getTableCellsHeaderSimple } from "shared/utils/dsComponentUtils";

const ContractList: React.FC<{}> = (pros) => {
  const { t } = useTranslation(["contract", "app"]);
  const appUserProfile: IAppUserProfile | undefined = useAppUser()?.userProfile;
  const [filters, setFilters] = useState<{ [key: string]: string }>({
    col1: "",
    col2: "",
  });
  const [contractList, setContractList] = useState<IContractElement[]>([]);
  const [contractListFiltered, setContractListFiltered] = useState<
    IContractElement[]
  >([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const requestStatus = useRequestStatus();

  const loadData = useCallback(() => {
    dispatch(requestPending());
    getContracts("anag")
      .then((response: any) => {
        setContractListFiltered(response.data);
        setContractList(response.data);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        dispatch(requestPristine());
      });
  }, [dispatch]);

  useEffect(() => {
    loadData();
    return () => {
      dispatch(requestPristine());
    };
  }, [loadData, dispatch]);

  const getBadgeStatus = (status: BadgeStatus) => {
    return status === BadgeStatus.VALID ? "confirm" : "error";
  };

  const tableCols = (() => {
    //return ["Codice", "Cliente", "Data inizio validità", "Data fine validità", ""];
    //return t("contract.contractList.tableColumns", { ns: 'contract', returnObjects: true }) as string[];
    return [
      t("contract.contractList.tableColumns.0", { ns: "contract" }) as string,
      t("contract.contractList.tableColumns.1", { ns: "contract" }) as string,
      t("contract.contractList.tableColumns.2", { ns: "contract" }) as string,
      t("contract.contractList.tableColumns.3", { ns: "contract" }) as string,
      t("contract.contractList.tableColumns.4", { ns: "contract" }) as string,
      t("contract.contractList.tableColumns.5", { ns: "contract" }) as string,
      t("contract.contractList.tableColumns.6", { ns: "contract" }) as string,
    ];
  })();

  const tableRows = contractListFiltered.map(
    (contract: IContractElement, index) => {
      return (
        <tr key={contract.contractId}>
          <td>
            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <DsBadge notificationStatus={getBadgeStatus(contract.status)} />
              {contract.contractCode}
            </span>
          </td>
          <td>{contract.businessName}</td>
          {/*<td>{moment.unix(contract.startDate).format(DATE_FORMAT)}</td>*/}
          <td>{parseDateForDS(moment.unix(contract.startDate).format(DATE_FORMAT),IParseDateFormatType.DATE_FORMAT1)}</td>
          {/*<td>{moment.unix(contract.endDate).format(DATE_FORMAT)}</td>*/}
          <td>{parseDateForDS(moment.unix(contract.endDate).format(DATE_FORMAT),IParseDateFormatType.DATE_FORMAT1)}</td>
          <td>{contract.slot}</td>
          <td>{contract.usedSlots}</td>
          <td>
            <TableRighArrow
              onClick={() => {
                navigate(contract.contractId);
              }}
            />
          </td>
        </tr>
      );
    }
  );

  const getContractListSearchColumns = (index: number) => {
    return index === 0 || index === 1 || index === 2 || index === 3;
  };

  // Gestione dei cambiamenti dei filtri
  const handleFilterChange = (columnKey: string, value: string) => {
    setFilters((prevFilters) => ({
        ...prevFilters,
        [columnKey]: value,
    }));
  };

  useEffect(() => {
    let myFilteredList: IContractElement[] = [];
    for (let i in contractList) {
      if (
        contractList[i].contractCode.toLocaleLowerCase().includes(filters.col1.toLocaleLowerCase()) &&
        contractList[i].businessName.toLocaleLowerCase().includes(filters.col2.toLocaleLowerCase())
      ) {
        myFilteredList.push(contractList[i]);
      }
    }
    setContractListFiltered(myFilteredList);
  }, [filters]);

  const [sortOrder, setSortOrder] = useState<{ [key: string]: 'asc' | 'desc' | null }>({
    column2: null, // Inizialmente null per capire se l'ordinamento è stato applicato
    column3: null, // Stessa cosa per la colonna 3
  });
  
  const handleSortColumn2 = () => {
    const sortedList = [...contractListFiltered].sort((a, b) => {
      const dateA = moment(a.startDate * 1000).format("DD.MM.YYYY");
      const dateB = moment(b.startDate * 1000).format("DD.MM.YYYY");
  
      // Se sortOrder.column2 è null, applica il primo ordinamento in ordine decrescente
      if (sortOrder.column2 === null) {
        return moment(dateB, "DD.MM.YYYY").unix() - moment(dateA, "DD.MM.YYYY").unix(); // Ordinamento decrescente per il primo clic
      }
  
      // Alterna tra crescente e decrescente
      return sortOrder.column2 === 'asc'
        ? moment(dateA, "DD.MM.YYYY").unix() - moment(dateB, "DD.MM.YYYY").unix()
        : moment(dateB, "DD.MM.YYYY").unix() - moment(dateA, "DD.MM.YYYY").unix();
    });
  
    setContractListFiltered(sortedList);
    setSortOrder((prev) => ({
      ...prev,
      column2: prev.column2 === null || prev.column2 === 'desc' ? 'asc' : 'desc', // Imposta 'asc' solo la prima volta, poi alterna
    }));
  };
  
  const handleSortColumn3 = () => {
    const sortedList = [...contractListFiltered].sort((a, b) => {
      const dateA = moment(a.endDate * 1000).format("DD.MM.YYYY");
      const dateB = moment(b.endDate * 1000).format("DD.MM.YYYY");
  
      // Se sortOrder.column3 è null, applica il primo ordinamento in ordine decrescente
      if (sortOrder.column3 === null) {
        return moment(dateB, "DD.MM.YYYY").unix() - moment(dateA, "DD.MM.YYYY").unix(); // Ordinamento decrescente per il primo clic
      }
  
      // Alterna tra crescente e decrescente
      return sortOrder.column3 === 'asc'
        ? moment(dateA, "DD.MM.YYYY").unix() - moment(dateB, "DD.MM.YYYY").unix()
        : moment(dateB, "DD.MM.YYYY").unix() - moment(dateA, "DD.MM.YYYY").unix();
    });
  
    setContractListFiltered(sortedList);
    setSortOrder((prev) => ({
      ...prev,
      column3: prev.column3 === null || prev.column3 === 'desc' ? 'asc' : 'desc', // Imposta 'asc' solo la prima volta, poi alterna
    }));
  };

      const getTableRowsContracts = (): ITableRow[] => {
              let myRetArr: ITableRow[] = [];
              for (let i in contractListFiltered) {
                  let tempObj: ITableRow = {
                      tableCells: [
                          { 
                              type: "text-badge", 
                              text: contractListFiltered[i].contractCode,
                              badgeStatus: contractListFiltered[i].status === 0 ?"confirm":"error"
                          },
                          { 
                              type: "text", 
                              text: contractListFiltered[i].businessName,
                          },
                          { 
                              type: "text", 
                              text: parseDateForDS(moment.unix(contractListFiltered[i].startDate).format(DATE_FORMAT),IParseDateFormatType.DATE_FORMAT1),
                          },
                          { 
                              type: "text", 
                              text: parseDateForDS(moment.unix(contractListFiltered[i].endDate).format(DATE_FORMAT),IParseDateFormatType.DATE_FORMAT1),
                          },
                          { 
                              type: "text", 
                              text: contractListFiltered[i].slot.toString(),
                          },
                          { 
                              type: "text", 
                              text: contractListFiltered[i].usedSlots.toString(),
                          },
                      ],
                      rightIcon: "icon-chevron-right",
                  };
                  myRetArr.push(tempObj);
              }
              return myRetArr;
      };

      const myTableConfigContracts: ITableConfig = {
              ...getDSTableSharedElements(560),
              tableHeader: {
                  tableCellsHeader: [
                      {
                          ...getDSTableShaderElementsForFilterColumn(t("contract.contractList.tableColumns.0", { ns: "contract" }) as string,"col1","20%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                          searchBoxValue:filters["col1"], //<---
                      },
                      {
                        ...getDSTableShaderElementsForFilterColumn(t("contract.contractList.tableColumns.1", { ns: "contract" }) as string,"col2","16%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                         searchBoxValue:filters["col2"], //<---
                      },
                      getTableCellsHeaderSimple(t("contract.contractList.tableColumns.2", { ns: "contract" }) as string,"col3","16%","left",true),
                      getTableCellsHeaderSimple(t("contract.contractList.tableColumns.3", { ns: "contract" }) as string,"col4","16%","left",true),
                      getTableCellsHeaderSimple(t("contract.contractList.tableColumns.4", { ns: "contract" }) as string,"col5","16%","left"),
                      getTableCellsHeaderSimple(t("contract.contractList.tableColumns.5", { ns: "contract" }) as string,"col6","16%","left"),
                     
                  ],
                  hasSearchBox: true,
              },
              tableRows: getTableRowsContracts()
          };

  return (
    <>
      <p className="pageTitle" style={{ marginBottom: "10px" }}>
        {t("contract.contractList.title", { ns: ["contract"] })}
      </p>
      {/*<p className="pageSubTitle">
        {t("contract.contractList.subTitle", { ns: ["contract"] })}
      </p>*/}

      {appUserProfile?.addEntityPolicy.includes(EntityType.CONTRACT) && (
        <div
          style={{
            marginTop: "28px",
            marginBottom: "14px",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-end",
          }}
        >
          <AddEntityButton entityType={EntityType.CONTRACT} />
        </div>
      )}

      {contractList && contractList.length ? (
        <div className="listTableContainer">
          {/*<SimpleTable
            columns={tableCols}
            rows={tableRows}
            tableType={SimpleTableType.LIST}
            simpleTableFamily={SimpleTableFamily.CONTRACTLIST}
            getSearchColumns={getContractListSearchColumns}
            columnInputHandlers={[
              (value) => handleFilterChange("col1", value),
              (value) => handleFilterChange("col2", value),
              handleSortColumn2,
              handleSortColumn3,
              undefined,
              undefined,
            ]}
            directInputColumns={[2,3]}
          />*/}
            {
              <Table 
                  tableConfig={myTableConfigContracts} 
                  style={{}}
                  onInputSearchBarChange={(event:Event)=>{
                      console.log((event as CustomEvent).detail.searchedText + " - " + (event as CustomEvent).detail.cells)
                  }}
                  //FILTERS
                  onClickRightAction={(event:Event)=>{
                      //console.log(event)
                        navigate(contractListFiltered[(event as CustomEvent).detail.row].contractId);
                  }}
                  onSubmitSearchedText={(event:Event)=>{
                            const focusColtemp = (event as CustomEvent).detail.cells;
                            let focusCol = '';
                            switch (focusColtemp) {
                                case 0:
                                    focusCol = "col1";
                                    break;
                                case 1:
                                    focusCol = "col2";
                                    break;
                                default:
                                    break;
                            }
                            const focusValue = (event as CustomEvent).detail.searchedText;
                            handleFilterChange(focusCol, focusValue)
                        }}
                  onTableInputChange={(event:Event)=>{
                    //console.log(event)
                    const focusCol = (event as CustomEvent).detail.col;
                    const focusValue = (event as CustomEvent).detail.value;
                    //console.log(focusCol + " - " + focusValue)
                    //handleFilterChange(focusCol, focusValue)
                  }}
                  onColumnSorting={(event:Event)=>{
                      //console.log((event as CustomEvent).detail.columnID) 
                      switch((event as CustomEvent).detail.columnID){
                        case "col3":
                          handleSortColumn2()
                          break;
                        case "col4":
                          handleSortColumn3()
                          break;
                        default:  
                          break;
                      }
                  }}
                  onResetSearch={(event:Event)=>{
                            //console.log('cliccata cella: ' + (event as CustomEvent).detail.cells);
                            switch ((event as CustomEvent).detail.cells) {
                                case 0:
                                    handleFilterChange("col1", "");
                                    break;
                                case 1:
                                    handleFilterChange("col2", "");
                                    break;
                                default:
                                    break;
                            }
                        }}
              />
            }
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginTop: "24px",
            }}
          >
            <DsBadge
              notificationStatus={getBadgeStatus(BadgeStatus.VALID)}
              isSmall={true}
            />
            <span className="legendaLabel--contract">
              {t("contract.contractList.tableLegend.2")}
            </span>
            <DsBadge
              notificationStatus={getBadgeStatus(BadgeStatus.EXPIRED)}
              isSmall={true}
            />
            <span className="legendaLabel--contract">
              {t("contract.contractList.tableLegend.1")}
            </span>
          </div>
        </div>
      ) : requestStatus === RequestStatus.SUCCESS ? (
        <NoDataFound
          title={t("contract.contractList.noData.title")} //"Non sono presenti contratti"
          subTitle={t("contract.contractList.noData.subTitle")} //"Inserisci un nuovo contratto dal bottone predisposto"
        />
      ) : (
        <></>
      )}
    </>
  );
};

export default ContractList;
